import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Search, Loader2, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { formatPrice } from "@/lib/format";
import { useLanguage } from "@/context/LanguageContext";
import ImageUpload from "@/components/admin/ImageUpload";
import api, { apiError } from "@/lib/api";

const GOLD = [
  { value: "18k Gold", es: "Oro 18k", en: "18k Gold" },
  { value: "14k Gold", es: "Oro 14k", en: "14k Gold" },
  { value: "10k Gold", es: "Oro 10k", en: "10k Gold" },
];
const WATCH_MATERIALS = [
  ...GOLD,
  { value: "Ceramic", es: "Cerámica", en: "Ceramic" },
  { value: "Silver", es: "Plata", en: "Silver" },
  { value: "Steel", es: "Acero", en: "Steel" },
];

const SECTIONS = {
  men: {
    gender: "men",
    title: { es: "Inventario · Hombre", en: "Inventory · Men" },
    categories: [
      { value: "earrings", es: "Aretes", en: "Earrings" },
      { value: "necklaces", es: "Cadena", en: "Chain" },
      { value: "bracelets", es: "Pulsera", en: "Bracelet" },
      { value: "rings", es: "Anillo", en: "Ring" },
    ],
    materials: GOLD,
    materialLabel: { es: "Tipo de oro", en: "Gold type" },
  },
  women: {
    gender: "women",
    title: { es: "Inventario · Mujer", en: "Inventory · Women" },
    categories: [
      { value: "earrings", es: "Aretes", en: "Earrings" },
      { value: "necklaces", es: "Collares", en: "Necklaces" },
      { value: "bracelets", es: "Pulsera", en: "Bracelet" },
      { value: "rings", es: "Anillo", en: "Ring" },
    ],
    materials: GOLD,
    materialLabel: { es: "Tipo de oro", en: "Gold type" },
  },
  watches: {
    gender: "unisex",
    fixedCategory: "watches",
    title: { es: "Inventario · Relojes", en: "Inventory · Watches" },
    categories: [],
    materials: WATCH_MATERIALS,
    materialLabel: { es: "Tipo de material", en: "Material type" },
  },
};

export default function ProductManager({ section }) {
  const cfg = SECTIONS[section];
  const { lang } = useLanguage();
  const es = lang === "es";
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const emptyForm = () => ({
    name: "",
    category: cfg.fixedCategory || cfg.categories[0]?.value || "",
    material: cfg.materials[0]?.value || "",
    description: "",
    price: "",
    sale_price: "",
    images: [],
    featured: false,
    available: true,
  });
  const [form, setForm] = useState(emptyForm());

  const load = () => {
    setLoading(true);
    api.get("/admin/products").then((r) => {
      let list = r.data.products;
      if (cfg.fixedCategory) {
        list = list.filter((p) => p.category === cfg.fixedCategory);
      } else {
        list = list.filter((p) => p.category !== "watches" && (p.gender === cfg.gender || p.gender === "unisex"));
      }
      setProducts(list);
    }).finally(() => setLoading(false));
  };
  useEffect(load, [section]); // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => { setEditing(null); setForm(emptyForm()); setDialogOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name || "",
      category: p.category || (cfg.fixedCategory || cfg.categories[0]?.value),
      material: p.material || (p.materials && p.materials[0]) || cfg.materials[0]?.value,
      description: p.description || "",
      price: p.price ?? "",
      sale_price: p.sale_price ?? "",
      images: p.images || [],
      featured: !!p.featured,
      available: p.status !== "draft",
    });
    setDialogOpen(true);
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!form.images.length) { toast.error(es ? "Añade al menos una imagen" : "Add at least one image"); return; }
    setSaving(true);
    const payload = {
      name: form.name,
      category: cfg.fixedCategory || form.category,
      gender: cfg.gender,
      description: form.description,
      price: parseFloat(form.price) || 0,
      sale_price: form.sale_price === "" ? null : parseFloat(form.sale_price),
      material: form.material,
      materials: form.material ? [form.material] : [],
      images: form.images,
      featured: form.featured,
      stock: 99,
      status: form.available ? "active" : "draft",
    };
    try {
      if (editing) await api.put(`/admin/products/${editing.id}`, payload);
      else await api.post("/admin/products", payload);
      toast.success(editing ? (es ? "Producto actualizado" : "Product updated") : (es ? "Producto creado" : "Product created"));
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const doDelete = async () => {
    await api.delete(`/admin/products/${deleteId}`);
    toast.success(es ? "Producto eliminado" : "Product deleted");
    setDeleteId(null);
    load();
  };

  const toggleAvailable = async (p) => {
    const status = p.status === "draft" ? "active" : "draft";
    setProducts((prev) => prev.map((x) => (x.id === p.id ? { ...x, status } : x)));
    try {
      await api.put(`/admin/products/${p.id}`, { ...p, status });
      toast.success(status === "active" ? (es ? "Visible en la tienda" : "Visible in store") : (es ? "Oculto de la tienda" : "Hidden from store"));
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
      load();
    }
  };

  const catLabel = (val) => {
    const c = cfg.categories.find((x) => x.value === val);
    return c ? c[lang] : val;
  };

  const filtered = products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div data-testid={`admin-inventory-${section}`}>
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-4xl font-light">{cfg.title[lang]}</h1>
          <p className="text-muted-foreground text-sm mt-1">{products.length} {es ? "productos" : "products"}</p>
        </div>
        <Button className="rounded-none" onClick={openNew} data-testid="add-product-btn"><Plus className="h-4 w-4 mr-1" /> {es ? "Añadir producto" : "Add product"}</Button>
      </div>

      <div className="relative mb-5 max-w-sm">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={es ? "Buscar por nombre" : "Search by name"} className="rounded-none pl-9" data-testid="product-search" />
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        {loading ? (
          <div className="py-24 grid place-items-center"><Loader2 className="h-6 w-6 animate-spin text-gold" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-20 text-center text-muted-foreground" data-testid="product-empty">{es ? "No hay productos todavía." : "No products yet."}</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-muted-foreground">
                <th className="p-4 font-medium overline">{es ? "Producto" : "Product"}</th>
                {!cfg.fixedCategory && <th className="p-4 font-medium overline hidden md:table-cell">{es ? "Categoría" : "Category"}</th>}
                <th className="p-4 font-medium overline hidden md:table-cell">{cfg.materialLabel[lang]}</th>
                <th className="p-4 font-medium overline">{es ? "Precio" : "Price"}</th>
                <th className="p-4 font-medium overline text-center hidden sm:table-cell">{es ? "Visible" : "Visible"}</th>
                <th className="p-4 font-medium overline text-right">{es ? "Acciones" : "Actions"}</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-b border-border last:border-0 hover:bg-secondary/40" data-testid={`product-row-${p.id}`}>
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-11 h-14 bg-secondary overflow-hidden shrink-0"><img src={p.images?.[0]} alt={p.name} className="h-full w-full object-cover" /></div>
                      <div>
                        <p className="font-medium flex items-center gap-1.5">{p.name} {p.featured && <Star className="h-3 w-3 fill-gold text-gold" />}</p>
                        {p.status === "draft" && <span className="text-[10px] text-amber-500">{es ? "Oculto" : "Hidden"}</span>}
                      </div>
                    </div>
                  </td>
                  {!cfg.fixedCategory && <td className="p-4 hidden md:table-cell text-muted-foreground">{catLabel(p.category)}</td>}
                  <td className="p-4 hidden md:table-cell text-muted-foreground">{p.material}</td>
                  <td className="p-4">{p.sale_price ? <span className="text-gold">{formatPrice(p.sale_price)}</span> : formatPrice(p.price)}</td>
                  <td className="p-4 text-center hidden sm:table-cell">
                    <Switch checked={p.status !== "draft"} onCheckedChange={() => toggleAvailable(p)} data-testid={`toggle-available-${p.id}`} />
                  </td>
                  <td className="p-4">
                    <div className="flex justify-end gap-1">
                      <Button variant="ghost" size="icon" onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}><Pencil className="h-4 w-4" /></Button>
                      <Button variant="ghost" size="icon" onClick={() => setDeleteId(p.id)} className="hover:text-destructive" data-testid={`delete-product-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-none max-w-xl max-h-[90vh] overflow-y-auto" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? (es ? "Editar producto" : "Edit product") : (es ? "Nuevo producto" : "New product")}</DialogTitle>
            <DialogDescription className="sr-only">Product form</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label className="overline text-muted-foreground">{es ? "Nombre" : "Name"}</Label><Input required value={form.name} onChange={set("name")} className="rounded-none mt-1.5" data-testid="pf-name" /></div>

            <div className="grid sm:grid-cols-2 gap-4">
              {!cfg.fixedCategory && (
                <div>
                  <Label className="overline text-muted-foreground">{es ? "Categoría" : "Category"}</Label>
                  <Select value={form.category} onValueChange={(v) => setForm((f) => ({ ...f, category: v }))}>
                    <SelectTrigger className="rounded-none mt-1.5" data-testid="pf-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{cfg.categories.map((c) => <SelectItem key={c.value} value={c.value}>{c[lang]}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="overline text-muted-foreground">{cfg.materialLabel[lang]}</Label>
                <Select value={form.material} onValueChange={(v) => setForm((f) => ({ ...f, material: v }))}>
                  <SelectTrigger className="rounded-none mt-1.5" data-testid="pf-material"><SelectValue /></SelectTrigger>
                  <SelectContent>{cfg.materials.map((m) => <SelectItem key={m.value} value={m.value}>{m[lang]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div><Label className="overline text-muted-foreground">{es ? "Características" : "Characteristics"}</Label><Textarea rows={3} value={form.description} onChange={set("description")} className="rounded-none mt-1.5 resize-none" data-testid="pf-description" /></div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label className="overline text-muted-foreground">{es ? "Precio ($)" : "Price ($)"}</Label><Input type="number" step="0.01" required value={form.price} onChange={set("price")} className="rounded-none mt-1.5" data-testid="pf-price" /></div>
              <div><Label className="overline text-muted-foreground">{es ? "Precio de oferta ($)" : "Sale price ($)"}</Label><Input type="number" step="0.01" value={form.sale_price} onChange={set("sale_price")} className="rounded-none mt-1.5" data-testid="pf-sale-price" /></div>
            </div>

            <ImageUpload multiple value={form.images} onChange={(imgs) => setForm((f) => ({ ...f, images: imgs }))} label={es ? "Imágenes" : "Images"} testid="pf-images" />

            <div className="flex flex-wrap gap-6 border-t border-border pt-4">
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.featured} onCheckedChange={(v) => setForm((f) => ({ ...f, featured: v }))} data-testid="pf-featured" /> {es ? "Destacado" : "Featured"}</label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={form.available} onCheckedChange={(v) => setForm((f) => ({ ...f, available: v }))} data-testid="pf-available" /> {es ? "Visible en la tienda" : "Visible in store"}</label>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" className="rounded-none flex-1" disabled={saving} data-testid="save-product-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (editing ? (es ? "Guardar cambios" : "Save changes") : (es ? "Crear producto" : "Create product"))}</Button>
              <Button type="button" variant="outline" className="rounded-none" onClick={() => setDialogOpen(false)}>{es ? "Cancelar" : "Cancel"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="rounded-none">
          <AlertDialogHeader><AlertDialogTitle>{es ? "¿Eliminar este producto?" : "Delete this product?"}</AlertDialogTitle><AlertDialogDescription>{es ? "Esta acción no se puede deshacer." : "This action cannot be undone."}</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none">{es ? "Cancelar" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="rounded-none bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="confirm-delete-product">{es ? "Eliminar" : "Delete"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
